import type { TowerDesign } from "@/game/towerSpec";
import type { StackSection } from "@/game/towerSpec";

/**
 * Bloomfield 185 ft self-support tower (Sabre, Bloomfield CT), from the stamped
 * tnxTower structural analysis in reference_towers/02_engineering_drawings/
 * CT_Bloomfield_Sabre_185ft_lattice_structural.pdf.
 *
 * EXACT data (report text layer):
 *   - 187.30 ft (57.09 m) to top, THREE-LEGGED Sabre lattice, 15 sections.
 *   - A wide, stout heavy tower: face width 37.00 ft at the base, 18.50 ft at the
 *     top. Exact face width at the top of each section (ft):
 *       T15→35.67, T14→35.00, T13→33.67, T12→33.00, T11→31.67, T10→31.00,
 *       T9→29.67, T8→29.00, T7→27.67, T6→27.00, T5→25.00, T4→23.00, T3→21.00,
 *       T2→19.00, T1→18.50; base 37.00. Circumradius = face·0.3048/√3.
 *   - Members: legs A572-50, angle bracing A36. Angles present: L5×5×3⁄8, L4×4×5⁄16,
 *     L4×4×¼, L4×3½×5⁄16, L3½×3½×¼, L3×3×5⁄16, L3×3×¼, L1×1×⅛. Exact leg pipe
 *     sizes not tabulated → mapped to the nearest catalog pipe tier.
 *   - FOUNDATION (EXACT): three (3) 6-ft diameter × 5.5-ft deep reinforced
 *     concrete piers, one per leg.
 *   - Design: TIA-222-H, Exposure C; + 50 mph with ice.
 */

const R = (ft: number) => (ft * 0.3048) / Math.sqrt(3);
const M = (ft: number) => ft * 0.3048;

// [section top height (ft), face width at top (ft), leg tier, diagonal tier]
const rows: Array<[number, number, "legHeavy" | "legXL" | "legXXL", "braceMed" | "braceHeavy" | "angleL5"]> = [
  [15.63, 35.67, "legXXL", "angleL5"],
  [22.3, 35.0, "legXXL", "angleL5"],
  [35.63, 33.67, "legXXL", "angleL5"],
  [42.3, 33.0, "legXXL", "angleL5"],
  [55.63, 31.67, "legXL", "braceHeavy"],
  [62.3, 31.0, "legXL", "braceHeavy"],
  [75.63, 29.67, "legXL", "braceHeavy"],
  [82.3, 29.0, "legXL", "braceHeavy"],
  [95.63, 27.67, "legXL", "braceHeavy"],
  [102.3, 27.0, "legHeavy", "braceMed"],
  [122.3, 25.0, "legHeavy", "braceMed"],
  [142.3, 23.0, "legHeavy", "braceMed"],
  [162.3, 21.0, "legHeavy", "braceMed"],
  [182.3, 19.0, "legHeavy", "braceMed"],
  [187.3, 18.5, "legHeavy", "braceMed"],
];

const sections: StackSection[] = rows.map(([ft, face, leg, diag], i) => {
  const prevFt = i === 0 ? 0 : rows[i - 1]![0];
  return {
    toHeight: M(ft),
    topRadius: R(face),
    panels: ft - prevFt > 13 ? 2 : 1, // ~10 ft panels: split the 20 ft sections
    legSection: leg,
    braceSection: diag,
    legMaterial: "a572",
    braceMaterial: "a36",
    bracing: "X",
  };
});

export const design: TowerDesign = {
  id: "selfsupport-bloomfield-185ft",
  name: "Self-Support (Bloomfield 185 ft, Sabre)",
  description: "Real 185 ft stout triangular Sabre self-support lattice, Bloomfield CT (TIA-222-H).",
  author: "reference: CT Bloomfield Sabre 185 ft tnxTower SA",
  spec: {
    type: "lattice",
    height: 57.09, // 187.30 ft
    segments: {
      faces: 3,
      baseRadius: R(37), // 37 ft base face → 6.51 m circumradius (stout)
      sections,
    },
    foundation: {
      soil: "dense-sand",
      // EXACT: three 6 ft dia × 5.5 ft deep reinforced concrete piers
      base: { type: "pier", width: 1.829, diameter: 1.829, depth: 1.676, concrete: "c30" },
      connection: {
        kind: "anchorBolts", nBolts: 8, bolt: "b1_75", grade: "f1554_55",
        boltCircleDia: 0.9, embedment: 0.7, edgeDistance: 0.4,
        platePlanArea: 0.7, plateThickness: 0.05, plateGrade: "a36rod", fc: 30e6,
      },
    },
  },
};
